import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Session, SupabaseClient } from "@supabase/supabase-js";
import { Crm, friendlyError } from "./crm";
import type { Membership, TenantRow } from "./types";
import { applyBrand } from "./brand";

// ------------------------------------------------------------------ auth
interface AuthValue {
  client: SupabaseClient;
  session: Session | null;
  ready: boolean;
}

const AuthContext = createContext<AuthValue | null>(null);

export function AuthProvider({ client, children }: { client: SupabaseClient; children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    client.auth.getSession().then(({ data }) => {
      if (!alive) return;
      setSession(data.session);
      setReady(true);
    });
    const { data } = client.auth.onAuthStateChange((_event, s) => {
      setSession(s);
      setReady(true);
    });
    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, [client]);

  const value = useMemo(() => ({ client, session, ready }), [client, session, ready]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** The signed-in session where there is one; null outside the AuthProvider (a screen rendered on its own in a test). */
export function useOptionalAuth(): AuthValue | null {
  return useContext(AuthContext);
}

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

// ------------------------------------------------------------------ workspace (the user's business)
export type WorkspaceStatus = "loading" | "none" | "ready" | "error";

export interface WorkspaceValue {
  status: WorkspaceStatus;
  error: string | null;
  membership: Membership | null;
  tenant: TenantRow | null;
  crm: Crm | null;
  refresh: () => Promise<void>;
}

export const WorkspaceContext = createContext<WorkspaceValue | null>(null);

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const { client, session } = useAuth();
  const [state, setState] = useState<Omit<WorkspaceValue, "refresh">>({
    status: "loading",
    error: null,
    membership: null,
    tenant: null,
    crm: null,
  });
  const userId = session?.user.id;
  const email = session?.user.email ?? "";

  const refresh = useCallback(async () => {
    if (!userId) return;
    try {
      // RLS: a user only sees their own memberships. The first one is the tenant crm_current_tenant() resolves to.
      const { data, error } = await client.from("tenant_members").select("*").eq("user_id", userId).order("created_at").limit(1);
      if (error) throw error;
      const membership = (data?.[0] ?? null) as Membership | null;
      if (!membership) {
        setState({ status: "none", error: null, membership: null, tenant: null, crm: null });
        return;
      }
      const crm = new Crm(client, membership.tenant_id, email, userId);
      const tenant = await crm.tenant();
      applyBrand(tenant?.config ?? null);
      setState({ status: "ready", error: null, membership, tenant, crm });
    } catch (e) {
      setState((s) => ({ ...s, status: "error", error: friendlyError(e).message }));
    }
  }, [client, userId, email]);

  useEffect(() => {
    setState((s) => ({ ...s, status: "loading" }));
    void refresh();
  }, [refresh]);

  const value = useMemo(() => ({ ...state, refresh }), [state, refresh]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceValue {
  const v = useContext(WorkspaceContext);
  if (!v) throw new Error("useWorkspace outside WorkspaceProvider");
  return v;
}

/** For screens inside the signed-in layout, where the workspace is known to be ready. */
export function useCrm(): Crm {
  const { crm } = useWorkspace();
  if (!crm) throw new Error("No workspace yet");
  return crm;
}
